// How a station command this member issued ended (spec/09 §5.1, §6.3): the api sends the issuing member
// `command.updated` on `member:{id}` as the desk device acks it, fails it, or it expires. Open drawer (F-OPS-09)
// follows its `station.drawer.open` this way. `null` until a frame for the command arrives — and when the socket is
// not live, in which case the console says only that the kick was sent.

import { channels, COMMAND_UPDATED } from '@clavian/contracts/constants';
import type { CommandUpdatedPayload } from '@clavian/contracts/realtime';
import type { TypeId } from '@clavian/contracts/typeid';
import { useState } from 'react';

import { useAccess } from '../../api/queries';
import { useChannelEvents, useRealtimeLive } from '../../realtime';

export type CommandStatus = CommandUpdatedPayload['status'];

/** Follows one command at a time: a new id starts over. */
export function useCommandStatus(venueId: string, commandId: string | null): CommandStatus | null {
  return useCommandUpdate(venueId, commandId)?.status ?? null;
}

/**
 * The last `command.updated` for one command — its status and what the station said (its `result` when acked, its
 * `error` when failed). Update now (F-STN-12) reads the station's answer from it. `null` until a frame arrives, and
 * when the socket is not live.
 */
export function useCommandUpdate(venueId: string, commandId: string | null): CommandUpdatedPayload | null {
  const live = useRealtimeLive();
  const access = useAccess({ venueId });
  const memberId = access.memberId;
  const [seen, setSeen] = useState<CommandUpdatedPayload | null>(null);

  useChannelEvents(
    channels.member((memberId ?? '') as TypeId<'mem'>),
    (envelope) => {
      if (envelope.type !== COMMAND_UPDATED) return;
      const payload = envelope.p as CommandUpdatedPayload | undefined;
      if (payload !== undefined && payload.commandId === commandId) setSeen(payload);
    },
    live && memberId !== null && commandId !== null,
  );

  return seen !== null && seen.commandId === commandId ? seen : null;
}
